import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { idOf, type Entity } from '../api.js';
import { t } from '../i18n.js';
import { formatDate, stateWithReason } from '../format.js';
import {
  ROOT,
  albums,
  axisOf,
  busy,
  excerptOf,
  expanded,
  folders,
  form,
  gridDensity,
  groups,
  items,
  lastFolder,
  me,
  months,
  searchQuery,
  searching,
  picked,
  selection,
  togglePicked,
  setGridDensity,
  setSpaceLayout,
  spaceIsGrid,
  toggleExpanded,
  treeNodes,
  view,
  visibleNodes,
} from '../state.js';
import {
  onAddMember,
  onCreateAlbum,
  onCreateGroup,
  onDeleteAlbum,
  onDeleteFolder,
  onNewFolder,
  onNewNote,
  onOpen,
  onSetContribPolicy,
  openView,
} from '../actions.js';
import { narrow } from './media.js';
import { Action, Empty } from './parts.js';
import { ObjectMenu } from './menu.js';
import { Thumb, VirtualGrid, filmstripOf, useLongPress } from './gallery.js';
import { MarkdownExcerpt } from '../markdown.js';
import { Filters } from './filters.js';
import { SelectionBar } from './picker.js';
import { Popover } from './popover.js';
import {
  IconCaret,
  IconChevron,
  IconCollection,
  IconDensityLarge,
  IconDensityMedium,
  IconDensitySmall,
  IconDoc,
  IconFolder,
  IconFolderOpen,
  IconGallery,
  IconList,
  IconMembers,
  IconMore,
  IconNote,
  IconTrash,
} from './icons.js';

/**
 * **Espace** — *où est-ce que je l'ai rangé ?*
 *
 * L'arborescence porte **tous les fichiers** depuis 4.5 — notes, documents,
 * photos et vidéos —, chacun rangé dans exactement un dossier, celui de son type
 * par défaut. Ce qu'elle ne porte **pas**, ce sont les collections : elles ne
 * sont pas rangées, et vivent dans *Mes partages*.
 *
 * Aucun filtre ne les écarte ici, et c'est voulu : elles n'ont plus de `Folder`,
 * donc le test d'appartenance à un dossier suffit. Une exception écrite serait
 * une exception de plus à tenir à jour.
 */

/**
 * L'arborescence, **pliable et signée** depuis 4.5 bis.
 *
 * Elle était jusqu'ici une liste où seule l'indentation disait la filiation :
 * aucun symbole, aucun moyen de refermer une branche. À dix dossiers c'était
 * lisible, à cinquante ce ne l'est plus — et un espace se remplit dans ce
 * sens-là. Trois choses changent, et une seule est décorative :
 *
 * - **le chevron**, qui n'apparaît que sur un dossier qui a des enfants — sa
 *   présence est donc en soi l'information « il y a quelque chose dessous » ;
 * - **l'icône**, ouverte ou fermée, qui redit le même état à l'autre bout de la
 *   ligne, pour qui suit le nom plutôt que la marge ;
 * - le **compte**, inchangé, qui reste celui du serveur.
 *
 * Le pli et la navigation sont **deux gestes distincts** : cliquer le nom ouvre
 * le dossier, cliquer le chevron déplie sans naviguer. Les confondre obligerait à
 * quitter ce qu'on regarde pour voir ce qu'un dossier contient.
 */
export function Tree({
  /** Appelé après une navigation : la feuille du mobile s'y referme. */
  onNavigate,
}: {
  readonly onNavigate?: () => void;
} = {}): JSX.Element {
  const current = view.value;
  const nodes = visibleNodes(treeNodes(folders.value, t('space.root')), expanded.value);

  return (
    <nav class="tree" data-testid="tree" aria-label={t('nav.space')}>
      <ul>
        {nodes.map((node) => {
          const selected = current.kind === 'folder' && current.folder === node.folder;
          const open = expanded.value.has(node.folder);
          return (
            <li key={node.folder} class="tree-row">
              {/* Le chevron est un bouton à part entière, et non une zone du
                  premier : un `<button>` dans un `<button>` est invalide, et le
                  clic y devient ambigu. */}
              {node.hasChildren ? (
                <button
                  type="button"
                  class="tree-twisty"
                  data-testid={`twisty-${node.folder}`}
                  aria-expanded={open}
                  aria-label={t(open ? 'space.collapse' : 'space.expand', { name: node.label })}
                  onClick={() => toggleExpanded(node.folder)}
                >
                  <IconChevron size={16} />
                </button>
              ) : (
                /* **Une classe à elle seule, et non `tree-twisty empty`.** Ce
                   cale-chevron composait avec `empty`, qui est *aussi* le bloc
                   des états vides — `padding: 48px 16px`. À spécificité égale,
                   c'est le dernier écrit qui gagnait : chaque dossier **sans
                   enfants** portait donc une cale de 98 px de haut, et
                   l'arborescence s'aérait par accident, d'une hauteur qui
                   dépendait de la position des règles dans la feuille. */
                <span class="tree-blank" aria-hidden="true" />
              )}
              <button
                type="button"
                class={selected ? 'tree-item current' : 'tree-item'}
                // L'indentation passe par un attribut et non par un `style` en
                // ligne : la CSP est `style-src 'self'`, qui refuse aussi les
                // attributs. La profondeur est bornée à 10 par le serveur
                // (`folder_too_deep`), donc énumérable en CSS.
                data-depth={Math.min(node.depth, 10)}
                data-testid={`folder-${node.folder}`}
                title={node.folder}
                aria-current={selected ? 'page' : undefined}
                disabled={Boolean(busy.value)}
                onClick={() => {
                  openView({ kind: 'folder', folder: node.folder });
                  onNavigate?.();
                }}
              >
                <span class="tree-glyph" aria-hidden="true">
                  {open && node.hasChildren ? <IconFolderOpen size={16} /> : <IconFolder size={16} />}
                </span>
                <span class="tree-label">{node.label}</span>
                {/* **Le compte est celui de la branche**, pas du dossier seul.
                    Un `/Photos` qui range tout par année affichait « 0 » avec
                    quatre mille photos dessous : un chiffre juste au sens du
                    serveur, et faux au sens de la question qu'on lui pose. Le
                    total est calculé sur la liste déjà en main — `GET /folders`
                    rend l'arborescence entière — donc **sans un appel de plus**.
                    Le détail reste lisible dans l'info-bulle, là où il ne coûte
                    rien à la ligne. */}
                <span
                  class="tree-count"
                  title={
                    node.totalCount === node.itemCount
                      ? undefined
                      : t('space.countDetail', { own: node.itemCount, total: node.totalCount })
                  }
                >
                  ({node.totalCount})
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * Les deux formulaires de création, **au même endroit et jamais tous les deux**.
 *
 * Ils vivaient l'un dans le panneau d'arborescence, l'autre sous le titre : deux
 * champs de même nature, à deux endroits, selon ce qu'on créait. Ils naissent
 * désormais du même « + », donc ils s'affichent au même endroit — sous la barre
 * d'actions qui les a ouverts, là où le regard vient de passer.
 */
export function CreationForm(): JSX.Element | null {
  if (form.value === 'folder') return <FolderForm />;
  if (form.value === 'note') return <NoteForm />;
  // Les trois formulaires des partages (4.6) vivent au même endroit, sous la même
  // barre, ouverts par le même « + » : c'est la règle de 4.5 bis, et un écran qui
  // s'en exempterait redeviendrait le patchwork qu'elle a supprimé.
  if (form.value === 'group' || form.value === 'collection' || form.value === 'invite') {
    return <ShareForm />;
  }
  return null;
}

function FolderForm(): JSX.Element {
  const base = view.value.kind === 'folder' && view.value.folder !== ROOT ? view.value.folder : '';
  const [value, setValue] = useState(`${base}/`);
  const submit = () => void onNewFolder(value.trim());

  return (
    <div class="form" data-testid="form-folder">
      <input
        class="input"
        data-testid="folder-input"
        aria-label={t('space.newFolder')}
        placeholder={t('space.folderPlaceholder')}
        autoFocus
        value={value}
        onInput={(ev) => setValue((ev.currentTarget as HTMLInputElement).value)}
        // Entrée valide : un formulaire à un champ où il faut viser un bouton
        // est un formulaire qu'on remplit deux fois.
        onKeyDown={(ev) => {
          if ((ev as KeyboardEvent).key === 'Enter') submit();
        }}
      />
      <Action testId="folder-submit" variant="primary" onClick={submit}>
        {t('space.create')}
      </Action>
    </div>
  );
}

/**
 * **Créer un groupe, une collection, inviter** — un champ, une touche Entrée.
 *
 * Les trois partagent la même forme parce qu'ils demandent la même chose : un seul
 * mot. Le champ dit lequel par son libellé, et le formulaire se ferme dès que
 * l'action part — l'écran qui apparaît derrière *est* la confirmation.
 */
function ShareForm(): JSX.Element {
  const kind = form.value;
  const [value, setValue] = useState('');
  const v = view.value;
  const gid = v.kind === 'group' ? v.gid : undefined;

  const submit = (): void => {
    const trimmed = value.trim();
    if (!trimmed) return;
    form.value = undefined;
    if (kind === 'group') void onCreateGroup(trimmed);
    else if (kind === 'collection' && gid) void onCreateAlbum(trimmed, gid);
    else if (kind === 'invite' && gid) void onAddMember(gid, trimmed);
  };

  const label =
    kind === 'group' ? t('group.newName') : kind === 'invite' ? t('group.invite') : t('collection.newName');

  return (
    <div class="form" data-testid={`form-${kind}`}>
      <input
        class="input"
        type={kind === 'invite' ? 'email' : 'text'}
        data-testid={`${kind}-input`}
        aria-label={label}
        placeholder={kind === 'invite' ? t('group.invitePlaceholder') : label}
        autoFocus
        value={value}
        onInput={(ev) => setValue((ev.currentTarget as HTMLInputElement).value)}
        onKeyDown={(ev) => {
          if ((ev as KeyboardEvent).key === 'Enter') submit();
        }}
      />
      <Action testId={`${kind}-submit`} variant="primary" onClick={submit}>
        {kind === 'invite' ? t('group.invite') : t('space.create')}
      </Action>
    </div>
  );
}

function NoteForm(): JSX.Element {
  const [value, setValue] = useState('');
  const submit = () => void onNewNote(value.trim());

  return (
    <div class="form" data-testid="form-note">
      <input
        class="input"
        data-testid="note-input"
        aria-label={t('space.newNote')}
        placeholder={t('space.notePlaceholder')}
        autoFocus
        value={value}
        onInput={(ev) => setValue((ev.currentTarget as HTMLInputElement).value)}
        onKeyDown={(ev) => {
          if ((ev as KeyboardEvent).key === 'Enter') submit();
        }}
      />
      <Action testId="note-submit" variant="primary" onClick={submit}>
        {t('space.create')}
      </Action>
    </div>
  );
}

/**
 * La barre de *Mon espace* — **deux contrôles, et plus cinq**.
 *
 * Elle en alignait cinq : l'axe, la présentation, la densité, la suppression du
 * dossier, et — sur téléphone seulement — un bouton « Dossiers » qui rappelait
 * l'arborescence. Le retour d'usage du 2026-08-09 a été « il y a une
 * multiplication de boutons », et il visait juste : trois d'entre eux réglaient
 * **la même chose**, comment la liste se peint, et l'un des cinq portait le même
 * mot qu'un segment voisin — « Dossiers » y désignait tantôt l'axe, tantôt le
 * panneau d'arborescence.
 *
 * Il en reste deux, et ils répondent aux deux seules questions de l'écran :
 *
 * - **où je regarde** — `LocationSwitch`, qui porte l'axe *et* le dossier ;
 * - **comment je regarde** — `DisplayMenu`, qui porte les lignes, les vignettes
 *   et leur densité.
 *
 * La suppression du dossier a rejoint le premier : elle agit sur le dossier
 * courant, donc elle appartient à l'endroit où l'on en change. L'ajout, lui,
 * avait déjà quitté cette barre pour le « + » unique.
 */
export function SpaceToolbar(): JSX.Element | null {
  const v = view.value;
  // **Une collection n'a pas de barre du tout** *(2026-08-15)*. Elle a reçu la
  // bascule d'affichage la veille — *comment je regarde* ne dépend pas de
  // l'endroit —, mais elle y était **seule sur sa rangée**, au-dessus d'une
  // seconde rangée qui ne portait que le bouton des filtres : deux lignes pour
  // deux boutons. Le retour d'usage : « les boutons filtres et grille/liste
  // devraient être sur la même ligne ». La bascule descend donc sur la ligne des
  // filtres (`SpaceView`), où il y a la place, et la rangée de commandes
  // disparaît. *Où je regarde* n'a de toute façon pas de sens ici : une
  // collection n'a ni axe ni dossier — c'est un ensemble, pas un emplacement.
  if (v.kind !== 'folder' && v.kind !== 'files') return null;
  return (
    <>
      <LocationSwitch />
      <DisplayToggle />
    </>
  );
}

/**
 * **Qui est dans le partage, et qui peut y verser** *(2026-08-16)*.
 *
 * Le réglage vivait dans la barre, sous une icône de deux personnages dont le
 * sens tenait dans une infobulle. Trois défauts en un, et le retour d'usage les
 * nomme tous les trois : « le petit icône est incompréhensible », « ce n'est pas
 * un *toggle*, on ne voit pas s'il est activé ou pas », « le tooltip ne marche
 * que sur un PC, sur mobile pas de survol ». Une fonction qui existe et qu'on ne
 * peut pas trouver n'existe pas.
 *
 * Elle descend donc **là où le partage se décrit**, en tête de son écran, dans le
 * bloc qui nomme déjà son audience. C'est le même geste que dans le sélecteur, où
 * le groupe est affiché sous chaque ligne : *à qui je donne* est la seule question
 * qu'un partage pose, et *qui peut y verser* en est la suite immédiate.
 *
 * Le bloc est visible de **tous** — un membre y lit la phrase qui lui dit ce qu'il
 * a le droit de faire. C'était le trou symétrique : rien, sur le site, ne le lui
 * apprenait, alors même que le sélecteur lui proposait le partage depuis toujours.
 *
 * ### Il **décrit**, il ne règle plus *(R3, 2026-09-12)*
 *
 * L'interrupteur y vivait, en tête d'écran et en permanence. Le retour d'usage :
 * « "autoriser les membres à contribuer" et "supprimer" sont toujours présents et
 * visibles en haut alors qu'il s'agit d'opérations d'admin plutôt rares qui
 * devraient être masquées par défaut ». C'est exact, et la séparation à faire
 * n'est pas *visible / caché* mais **état / geste** : ce qu'un partage est se lit
 * sans rien ouvrir, ce qu'on lui fait passe par un menu.
 *
 * La phrase d'état reste donc ici, **pour le propriétaire aussi** — sans elle, il
 * perdrait le seul endroit où il lisait le réglage, et un geste replié dont l'état
 * n'est nulle part est pire que le bouton qu'on vient d'enlever. Le geste, lui,
 * descend dans `CollectionToolbar`.
 */
export function CollectionAudience(): JSX.Element | null {
  const v = view.value;
  if (v.kind !== 'collection') return null;

  const album = (albums.value ?? []).find((a) => a.Aid === v.aid);
  // Tant que `GET /albums` n'a pas répondu, on ne montre rien plutôt que de
  // supposer la propriété : un bloc qui apparaît puis change de contenu est pire
  // qu'un bloc qui arrive une fois.
  if (!album) return null;

  const known = groups.value ?? me.value?.groups ?? [];
  const audience = (album.authGroups ?? [])
    .map((gid) => known.find((g) => g.gid === gid)?.name ?? gid)
    .join(', ');
  const collective = album.ContribPolicy === 'members';
  const mine = me.value !== undefined && album.Owner === me.value.uid;

  return (
    <div class="audience" data-testid="collection-audience">
      <p class="audience-line">
        <span>{t('collection.group')} :</span>
        <strong data-testid="audience-groups">{audience || '—'}</strong>
      </p>
      {mine ? (
        // Le propriétaire lit l'**état**, et rien de plus : le geste est dans le
        // menu `…` de la barre. `data-testid` conservé du temps de l'interrupteur
        // aurait menti sur ce qu'il désigne — c'est une phrase, plus un contrôle.
        <p class="audience-line" data-testid="collection-contrib-state">
          {collective ? t('collection.contribOn') : t('collection.contribOff')}
        </p>
      ) : collective ? (
        <p class="audience-line" data-testid="collection-collective">
          {t('collection.collectiveNotice')}
        </p>
      ) : null}
    </div>
  );
}

/**
 * **La barre d'une collection** — les deux gestes d'administration, repliés
 * *(R3, 2026-09-12)*.
 *
 * Elle n'existait pas avant le 2026-08-16, et une capacité du socle n'était donc
 * atteignable depuis aucun écran : `DELETE /albums/{aid}`, servie depuis l'étape 1.
 *
 * Elle est réservée au **propriétaire**, comme les deux routes. Un membre n'a pas à
 * voir des gestes qu'on lui refuserait — proposer pour voir échouer est pire que de
 * ne pas proposer, c'est déjà la règle de la liste des membres.
 *
 * ### Pourquoi un menu, et pas un bouton de moins
 *
 * Le retour d'usage vise les deux gestes ensemble : « toujours présents et visibles
 * en haut alors qu'il s'agit d'opérations d'admin plutôt rares qui devraient être
 * masquées par défaut ». Ils partagent une propriété — on les fait une fois dans la
 * vie d'un partage — et une seule des deux détruit, si bien que « replier ce qui est
 * dangereux » n'aurait replié que la moitié du grief.
 *
 * C'est donc **la rareté** qui décide, et non le danger, et le motif existe déjà :
 * le `…` de la barre de sélection. Il y avait été ouvert pour une autre raison —
 * qu'aucun doigt n'atteigne la suppression par accident — et son commentaire notait
 * qu'une seconde entrée lui ferait perdre cette qualité. Ici, la qualité voulue est
 * l'inverse : **un seul point d'entrée pour ce qui ne se fait presque jamais**.
 *
 * L'état, lui, ne se replie pas : la phrase qui dit si le partage est collectif
 * reste dans le bloc d'audience, au-dessus de la grille. Un réglage rangé dans un
 * menu doit pouvoir se **lire** sans ouvrir le menu — sans quoi on l'ouvre à chaque
 * fois, et le repli n'a rien gagné.
 *
 * La suppression garde sa confirmation **sur place** plutôt qu'en boîte de dialogue,
 * comme celle d'un groupe, et sa phrase dit ce qui est vraiment coupé : le serveur
 * révoque toutes les arêtes d'accès que la collection avait propagées. Ce qu'elle
 * contenait, lui, reste — c'est « retirer n'est pas supprimer » appliqué à
 * l'ensemble entier, et c'est exactement ce dont on doute au moment d'appuyer.
 */
export function CollectionToolbar(): JSX.Element | null {
  const v = view.value;
  const [confirm, setConfirm] = useState(false);

  if (v.kind !== 'collection') return null;

  const album = (albums.value ?? []).find((a) => a.Aid === v.aid);
  if (!album || !me.value || album.Owner !== me.value.uid) return null;

  const collective = album.ContribPolicy === 'members';

  if (confirm) {
    return (
      <div class="confirm">
        <span class="confirm-text">{t('collection.confirmDelete')}</span>
        <Action
          testId="confirm-delete-collection"
          variant="danger"
          onClick={() => {
            setConfirm(false);
            void onDeleteAlbum(String(v.aid));
          }}
        >
          {t('collection.delete')}
        </Action>
        <Action testId="cancel-delete-collection" variant="quiet" onClick={() => setConfirm(false)}>
          {t('app.cancel')}
        </Action>
      </div>
    );
  }

  return (
    <Popover
      testId="collection-admin"
      label={t('collection.admin')}
      width={264}
      align="end"
      trigger={() => <IconMore size={18} />}
    >
      {(close) => (
        <>
          {/* **Le libellé dit ce que le clic fait**, jamais l'état qu'on quitte :
              une entrée de menu est un verbe. L'état, lui, se lit au-dessus de la
              grille — c'est la condition pour que ce repli soit un gain. */}
          <button
            type="button"
            role="menuitem"
            data-testid="toggle-contrib"
            onClick={() => {
              close();
              void onSetContribPolicy(String(v.aid), collective ? 'owner' : 'members');
            }}
          >
            <IconMembers size={18} />
            <span>
              {collective ? t('collection.contribRestrict') : t('collection.contribToggle')}
            </span>
          </button>
          <button
            type="button"
            role="menuitem"
            class="danger"
            data-testid="delete-collection"
            onClick={() => {
              close();
              setConfirm(true);
            }}
          >
            <IconTrash size={18} />
            <span>{t('collection.delete')}</span>
          </button>
        </>
      )}
    </Popover>
  );
}

/**
 * **Où je regarde** — l'axe, et le dossier, dans le même contrôle.
 *
 * Le segment restait juste : *par dossier* ou *tout par date* sont deux façons de
 * lire les mêmes objets, et un segment dit **où l'on est** là où une bascule dirait
 * seulement ce qu'on obtiendrait. Le retour d'usage l'a confirmé — « le choix
 * dossier/tout est très clair visuellement ».
 *
 * Ce qui manquait est ailleurs : **changer de dossier était un autre geste, dans un
 * autre contrôle** — le panneau de gauche sur un grand écran, et sur téléphone un
 * bouton qui ouvrait un tiroir, c'est-à-dire trois barres horizontales dans un coin
 * de la barre de titre. « Contre-intuitif », et c'est exact : le dossier courant
 * *est* la position sur l'axe *Dossiers*, il n'a aucune raison de se changer
 * ailleurs que là où l'axe se choisit.
 *
 * Le segment *Dossiers* porte donc **le nom du dossier courant** et un chevron :
 * quand on n'est pas sur cet axe il y bascule, quand on y est il déploie
 * l'arborescence. Un geste, deux effets, et jamais d'ambiguïté — l'effet dépend de
 * l'état que le segment affiche déjà.
 */
function LocationSwitch(): JSX.Element {
  const v = view.value;
  const axis = axisOf(v);
  const folder = v.kind === 'folder' ? v.folder : lastFolder.value;
  // Le **dernier segment** du chemin, jamais le chemin entier : `/Photos/2026/Été`
  // occuperait toute la barre pour une information que le titre porte déjà, en
  // entier et cliquable.
  const name = folder === ROOT ? t('space.axis.folder') : folder.slice(folder.lastIndexOf('/') + 1);
  // **Sur un téléphone, le segment reprend son mot** *(2026-08-15)*. Le nom du
  // dossier courant est monté dans la barre du haut ; l'écrire une seconde fois
  // ici donnerait deux fois la même information à trois centimètres d'écart, et
  // prendrait la largeur que les deux segments doivent justement occuper. Le
  // segment redevient ce qu'il est — un axe —, et son déploiement reste le seul
  // chemin vers un autre dossier.
  const label = narrow.value ? t('space.axis.folder') : name;

  return (
    <div class="segmented" role="group" aria-label={t('space.axis')}>
      {axis === 'folder' ? (
        <Popover
          testId="folder-picker"
          className="segment-popover"
          buttonClass="segment current"
          label={t('space.chooseFolder', { name })}
          title={t('space.chooseFolder', { name })}
          width={288}
          trigger={() => (
            <>
              <IconFolder size={16} />
              <span class="segment-label">{label}</span>
              <IconCaret size={14} />
            </>
          )}
        >
          {(close) => <FolderPanel onDone={close} />}
        </Popover>
      ) : (
        <button
          type="button"
          class="segment"
          data-testid="axis-folder"
          aria-pressed={false}
          title={t('nav.space.q')}
          onClick={() => openView({ kind: 'folder', folder: lastFolder.value })}
        >
          <IconFolder size={16} />
          <span>{label}</span>
        </button>
      )}
      <button
        type="button"
        class={axis === 'all' ? 'segment current' : 'segment'}
        data-testid="axis-all"
        aria-pressed={axis === 'all'}
        title={t('space.axis.allQ')}
        onClick={() => openView({ kind: 'files' })}
      >
        <IconGallery size={16} />
        <span>{t('space.axis.all')}</span>
      </button>
    </div>
  );
}

/**
 * Le contenu du déploiement : **l'arborescence, et le geste qui porte sur le
 * dossier qu'on regarde** — le supprimer.
 *
 * Il y est parce qu'il n'a de sens que devant la liste des dossiers ; il
 * occupait jusqu'ici un bouton de la barre principale, en permanence, pour un
 * geste rare et destructeur.
 *
 * **« Nouveau dossier » n'y est pas**, et ce n'est pas un oubli : la règle de
 * 4.5 bis dit un seul point d'ajout par écran, et c'est le « + ». Le remettre
 * ici pour la commodité ferait exactement le patchwork que cette règle a
 * supprimé — deux endroits pour créer, selon celui où l'on se trouvait.
 */
function FolderPanel({ onDone }: { readonly onDone: () => void }): JSX.Element {
  const v = view.value;
  const current = v.kind === 'folder' ? v.folder : undefined;
  return (
    <div class="folder-panel">
      <Tree onNavigate={onDone} />
      {current !== undefined && current !== ROOT ? (
        <>
          <hr />
          <button
            type="button"
            role="menuitem"
            class="danger"
            data-testid="delete-folder"
            onClick={() => {
              onDone();
              void onDeleteFolder(current);
            }}
          >
            <IconTrash size={18} />
            <span>{t('space.deleteFolder')}</span>
          </button>
        </>
      ) : null}
    </div>
  );
}

/**
 * **Comment je regarde** — un bouton, quatre positions, un tour.
 *
 * C'étaient deux boutons ; ce fut ensuite un menu de valeurs nommées, contre le
 * cycle, au motif qu'« un cycle oblige à cliquer pour découvrir ce qu'on obtient
 * et à recliquer trois fois pour revenir ». L'argument est juste sur le papier et
 * faux à l'usage, pour une raison que le papier ne dit pas : **le résultat est
 * l'écran lui-même**. On ne découvre pas ce qu'on obtient, on le voit — la grille
 * change sous le doigt, et un tour complet coûte trois appuis là où le menu en
 * coûtait deux *plus* une lecture. Le retour d'usage a été net : « je trouve le
 * menu pour passer de liste à grille trop compliqué ; un simple bouton à appuyer
 * plusieurs fois serait plus simple ». *(2026-08-15)*
 *
 * Les deux réglages n'en font plus qu'un — **lignes → grandes → moyennes →
 * petites**, et on repart aux lignes. C'est leur ordre naturel : chaque cran
 * montre plus d'objets et moins de détail que le précédent, si bien que le cycle
 * n'a pas d'ordre à apprendre, seulement un sens.
 *
 * *Automatique* disparaît de la façade : c'est l'état de départ, pas une position
 * qu'on vise. Le premier appui prend l'état effectif et le rend explicite, ce qui
 * est exactement ce qu'on demande à un bouton — que la suite ne dépende plus de
 * ce que le contenu décide.
 */
function DisplayToggle(): JSX.Element {
  const grid = spaceIsGrid.value;
  const density = gridDensity.value;

  // L'icône dit **ce qu'on voit**, jamais ce qu'on obtiendrait au prochain appui :
  // c'est un état. Le libellé, lui, nomme l'état courant — sur un cycle, savoir
  // où l'on est suffit à savoir où l'on va.
  const Icon = !grid
    ? IconList
    : density === 'large'
      ? IconDensityLarge
      : density === 'medium'
        ? IconDensityMedium
        : IconDensitySmall;
  const label = grid ? t(`space.density.${density}`) : t('space.layout.list');

  const next = (): void => {
    if (!grid) {
      setSpaceLayout('grid');
      return setGridDensity('large');
    }
    if (density === 'large') return setGridDensity('medium');
    if (density === 'medium') return setGridDensity('small');
    setSpaceLayout('list');
  };

  return (
    <button
      type="button"
      class="btn quiet display-toggle"
      data-testid="display-menu"
      title={t('space.displayCycle', { current: label })}
      aria-label={t('space.displayCycle', { current: label })}
      onClick={next}
    >
      <Icon size={18} />
      <span class="label-wide">{label}</span>
    </button>
  );
}

/**
 * **Un dossier se peint selon ce qu'il contient.**
 *
 * Il ne savait faire que des lignes : deux photos y étaient deux paragraphes de
 * texte avec, pour toute image, une icône générique de 36 px. Sur un produit dont
 * le sujet est l'image, c'était le défaut le plus visible de l'écran — et il
 * précède de loin toute question de palette.
 *
 * Les tuiles sont **exactement celles de la photothèque**, virtualisation
 * comprise : une seconde tuile, même proche, aurait divergé au premier
 * raffinement, et un dossier peut porter autant de choses que la bibliothèque.
 * Ce qui change est le groupement — un dossier est un lieu, il n'a pas de mois.
 */
export function SpaceView(): JSX.Element {
  // **La phrase de la collection est partie** *(2026-08-15)*. « Retirer un
  // élément d'une collection ne le supprime pas » occupait une ligne au-dessus de
  // chaque collection ouverte, en permanence, pour lever un doute qu'on n'a
  // **qu'au moment de retirer**. Le retour d'usage : « le texte est inutile ». Il
  // l'est là où il était ; il ne l'est pas là où le geste se pose — le menu
  // d'objet et la fiche portent « Retirer », et c'est là que la phrase se lit
  // désormais (`collection.removeIsNotDelete`, en note sous le bouton).
  const collection = view.value.kind === 'collection';
  return (
    <>
      {/* **Les filtres valent aussi dans un dossier** *(2026-08-09)*. Ils
          n'existaient que sur l'axe *Tout*, c'est-à-dire que « montre-moi mes
          vidéos » dépendait de l'écran d'où on la posait. Ils sont repliés par
          défaut : cinq champs avant la première photo, c'est une photothèque
          qu'on ouvre sans voir une seule photo.

          **Et dans une collection** *(2026-08-14)* : elle avait son propre corps,
          une grille et rien d'autre — ni filtres, ni bascule lignes/vignettes, ni
          densité. Ce sont les mêmes objets ; leur donner deux corps faisait
          dépendre du lieu ce qui ne dépend que de l'objet. C'est la correction
          que les deux axes de *Mon espace* avaient déjà reçue le 2026-08-09, et
          la collection en était restée exclue. */}
      {/* Dans une collection, la bascule d'affichage **partage la ligne des
          filtres** : elle n'a pas de rangée de commandes où se poser, et une
          rangée pour un seul bouton est une rangée de trop. */}
      <Filters trailing={collection ? <DisplayToggle /> : undefined} />
      <SpaceBody />
    </>
  );
}

/**
 * **Le corps de *Mon espace*, pour ses deux axes.**
 *
 * L'axe *Tout* avait son propre corps — `GalleryView`, une grille et rien
 * d'autre. Les deux axes portent pourtant les mêmes objets ; la conséquence
 * était qu'une préférence d'affichage valait d'un côté et pas de l'autre, ce que
 * le retour d'usage a relevé sur les deux clients. Un seul corps, donc, et
 * l'axe ne décide plus que du **groupement** — que `months` porte déjà, en se
 * repliant tout seul sur un groupe unique quand le tri n'est pas chronologique.
 */
export function SpaceBody(): JSX.Element {
  const kind = view.value.kind;
  if (items.value.length === 0 && searching.value) {
    // **« Aucune photo » est une réponse** *(R7)*, et elle ne doit pas se lire
    // comme un dossier vide : le dossier n'est pas vide, rien n'y correspond.
    return (
      <ul class="items" data-testid={kind === 'collection' ? 'gallery' : 'items'}>
        <Empty as="li">
          <span data-testid="search-none">{t('search.none', { query: searchQuery.value.trim() })}</span>
        </Empty>
      </ul>
    );
  }
  if (items.value.length === 0) {
    // Trois vides, trois phrases : un dossier vide, une photothèque vide et une
    // collection vide ne se corrigent pas de la même façon.
    const empty =
      kind === 'collection'
        ? 'collection.empty'
        : kind === 'files'
          ? 'state.emptyGallery'
          : 'state.emptyFolder';
    return (
      <ul class="items" data-testid={kind === 'collection' ? 'gallery' : 'items'}>
        <Empty as="li">{t(empty)}</Empty>
      </ul>
    );
  }

  if (spaceIsGrid.value) {
    return (
      <>
        <SelectionBar />
        <VirtualGrid groups={months.value} />
      </>
    );
  }

  // **La sélection existe aussi en lignes** *(2026-08-15)*. Elle n'y existait
  // pas : « une ligne n'a ni case ni appui long, donc rien ne permet d'en cocher
  // une seule ». C'était vrai, et c'était le défaut — le retour d'usage l'a dit
  // sans détour, « la sélection ne fonctionne pas sur les listes ». Une
  // présentation ne décide pas des gestes qu'on peut faire sur un objet : c'est
  // exactement la divergence que le menu d'objet unique existe pour empêcher, et
  // elle valait encore pour le geste le plus courant.
  //
  // Dans une collection, le menu d'objet gagne « Retirer » — la grille le faisait
  // déjà, les lignes non.
  const album = view.value.kind === 'collection' ? view.value.aid : undefined;
  return (
    <>
      <SelectionBar />
      <ul
        class={picked.value.size > 0 ? 'items picking' : 'items'}
        data-testid={kind === 'collection' ? 'gallery' : 'items'}
      >
        {items.value.map((entity) => (
          <Row key={idOf(entity)} entity={entity} collection={album} />
        ))}
      </ul>
    </>
  );
}

/** Une ligne de listing — le rendu commun aux dossiers. */
function Row({
  entity,
  collection,
}: {
  readonly entity: Entity;
  readonly collection?: string;
}): JSX.Element {
  const id = idOf(entity);
  // **Une collection ne se coche pas** : la sélection multiple porte des `fid`,
  // et les gestes qu'elle propose — ranger, supprimer en lot — n'ont pas de sens
  // sur un ensemble. Elle garde son menu d'objet.
  const fid = entity.Fid;
  const chosen = fid !== undefined && picked.value.has(fid);
  const picking = picked.value.size > 0;
  const longPress = useLongPress(() => fid !== undefined && togglePicked(fid));
  const meta = [kindLabel(entity), formatDate(entity.DocumentDate ?? entity.CreatedAt)];
  // L'état n'est affiché que lorsqu'il est autre chose que « prêt » : le montrer
  // toujours ferait du cas nominal une information, et de l'anomalie du bruit.
  if (entity.State && entity.State !== 'READY')
    meta.push(stateWithReason(entity.State, entity.FailReason));

  return (
    <li
      data-testid={`item-${id}`}
      class={[selection.value === id ? 'selected' : '', chosen ? 'picked' : '']
        .filter(Boolean)
        .join(' ')}
      {...(fid === undefined ? {} : longPress.handlers)}
    >
      {/* La case n'apparaît **qu'en sélection**, comme sur une tuile : c'est
          l'appui long qui ouvre le mode, et une case permanente sur chaque ligne
          serait le chrome qu'on a retiré de la grille. */}
      {picking && fid !== undefined ? (
        <label class="row-pick" title={t('gallery.pick')}>
          <input
            type="checkbox"
            data-testid={`pick-${fid}`}
            checked={chosen}
            onChange={() => togglePicked(fid)}
          />
        </label>
      ) : null}
      {/* **La ligne entière s'ouvre.** Une cible de clic réduite au nom oblige à
          viser ; et jusqu'à 4.2, rien ne s'ouvrait du tout. */}
      <button
        type="button"
        class="row-open"
        data-testid={`open-${id}`}
        onClick={() => {
          // L'appui long vient de cocher : le clic que le navigateur émet
          // derrière ouvrirait la chose qu'on voulait seulement sélectionner.
          if (longPress.fired.current) {
            longPress.fired.current = false;
            return;
          }
          // Tant qu'on coche, on coche — la règle de la grille, et celle de
          // toutes les listes qui savent sélectionner.
          if (picking && fid !== undefined) return togglePicked(fid);
          onOpen(entity, items.value);
        }}
      >
        <RowGlyph entity={entity} />
        <span class="grow">
          <span class="row-name" data-testid={`item-name-${id}`}>
            {entity.Name}
          </span>
          <span class="row-meta">{meta.join(' · ')}</span>
        </span>
      </button>
      {/* **Le même menu que sur une tuile.** L'arborescence n'offrait qu'une
          suppression, la photothèque une inspection et une suppression, et
          l'ajout à une collection n'existait que dans la barre de sélection :
          trois inventaires d'actions pour les mêmes objets. */}
      <ObjectMenu entity={entity} id={id} collection={collection} />
    </li>
  );
}

export function kindLabel(entity: Entity): string {
  if (entity.Aid !== undefined) return t('kind.collection', { count: entity.ItemCount ?? 0 });
  if (entity.Type === 'NOTE') return t('kind.note');
  if (entity.Type === 'DOC') return t('kind.doc');
  return entity.Kind === 'VID' ? t('kind.video') : t('kind.photo');
}

/**
 * **La pastille d'une ligne montre l'image quand il y en a une.**
 *
 * Elle portait une icône générique de type — la même pour deux photos, pour deux
 * vidéos, pour tout. En liste comme en grille, la première chose qu'on cherche
 * dans un fichier d'images est l'image ; l'icône ne servait qu'à ceux qui n'en
 * ont pas. Le carré fait 36 px et la vignette existe déjà : il n'y a ni requête
 * ni octet de plus, `thumbs.ts` mémorise les URL présignées entre les deux vues.
 *
 * Le drapeau est `size` et non `State`, comme partout : un média `READY` pour sa
 * source peut n'avoir pas encore sa vignette, et demander l'URL d'un rôle sans
 * octets constatés ne rendrait pas une image mais une erreur.
 */
function RowGlyph({ entity }: { readonly entity: Entity }): JSX.Element {
  const fid = entity.Fid;
  if (fid && entity.Variants?.['thumb']?.size !== undefined) {
    return (
      <span class="row-thumb">
        <Thumb fid={fid} alt="" strip={filmstripOf(entity)} />
      </span>
    );
  }
  // **Une note montre son texte, pas une icône** *(2026-08-16)*. La rédaction
  // précédente posait un carré de papier **vide avec un pictogramme dedans** en
  // faisant valoir qu'à 36 px « deux lignes ne se lisent pas ». C'est vrai, et
  // c'est hors sujet : une vignette de note ne sert pas à *lire*, elle sert à
  // **reconnaître** — une liste de courses, un paragraphe et un titre suivi de
  // trois puces n'ont pas la même silhouette. Un pictogramme, lui, est le même
  // pour les douze notes de la liste. C'est le même argument que pour la tuile,
  // et le même moteur de rendu.
  const excerpt = entity.Aid === undefined ? excerptOf(entity) : undefined;
  if (excerpt) {
    return (
      <span class="row-glyph row-paper paper" aria-hidden="true">
        <MarkdownExcerpt source={excerpt} testId={`row-excerpt-${idOf(entity)}`} />
      </span>
    );
  }
  // Une note **encore vide** n'a pas de silhouette : elle garde son papier et son
  // pictogramme, qui disent au moins de quelle matière il s'agit.
  const paper = entity.Aid === undefined && entity.Type === 'NOTE';
  return (
    <span class={paper ? 'row-glyph row-paper' : 'row-glyph'} aria-hidden="true">
      <Glyph entity={entity} />
    </span>
  );
}

/** Le même dessin qu'ailleurs pour la même chose : la tuile, la ligne, l'app. */
export function Glyph({ entity }: { readonly entity: Entity }): JSX.Element {
  if (entity.Aid !== undefined) return <IconCollection size={18} />;
  if (entity.Type === 'NOTE') return <IconNote size={18} />;
  if (entity.Type === 'DOC') return <IconDoc size={18} />;
  return <IconGallery size={18} />;
}

